import axios, { CancelTokenSource } from "axios";
import * as bootstrap from "bootstrap";
import { DateTime } from "luxon";
import * as clipboard from '../clipboard';
import { Download } from "../download";
import * as html from '../html';
import Modal from "../modal";
import { G } from "../querystring";
import { RemToPx } from "../rem";
import * as strings from "../strings";
import * as toast from "../toast";
import * as column from './column';
import * as query from './query';

declare interface MyWindow extends Window {
    readonly WsRoot: string;
}
declare var window: MyWindow;

declare interface apiResponse {
    nextPage: boolean;
    returnedFields: string[];
}

function equal<R extends Object>(a: R, b: R): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(a);

    if (aKeys.length !== bKeys.length) {
        return false;
    }

    for (const k of aKeys) {
        if (JSON.stringify((a as any)[k]) !== JSON.stringify((b as any)[k])) {
            return false;
        }
    }

    return true;
}

class row<R extends GenericRow, C, D> {
    tr: HTMLTableRowElement | null = null;
    checkbox: HTMLInputElement | null = null;

    constructor(private table: Table<R, C, D>, public row: R, private tbody: HTMLTableSectionElement) { }

    Visible(): boolean {
        return this.tr?.style.display !== 'none' && this.tr?.parentElement === this.tbody;
    }

    Checked(): boolean {
        return this.table.IsChecked(this.row.id!);
    }
}

export declare interface ColumnStats {
    count: number;
    unique: number;
    sum: number;
    max: number;
    min: number;
    avg: number;
    trimmed10: number;
    trimmed20: number;
}

type id = string | number;

export interface GenericRow {
    id?: id;
    created?: string;
}

export class Table<R extends GenericRow, C, D> {
    RowsPerPage = 100;
    private page = Number(G().Get('p')) || 0;

    private columns: column.Column<R, C, D>[] = [];
    Query = new query.Query(this.SearchInput, this.SearchForm);

    // UI controls
    private reload = document.getElementById('reload')! as HTMLAnchorElement;

    private firstPage = Array.from(document.querySelectorAll('.first-page'));
    private nextPage = Array.from(document.querySelectorAll('.next-page'));
    private prevPage = Array.from(document.querySelectorAll('.prev-page'));

    private startingRowNumber = document.getElementById('starting-row-number')!;
    private endingRowNumber = document.getElementById('ending-row-number')!;

    private searchForm = document.getElementById('search-form') as HTMLFormElement;

    private table = document.getElementById('table') as HTMLTableElement;
    private thead = this.table.querySelector('thead') as HTMLTableSectionElement;
    private tbody = this.table.querySelector('tbody') as HTMLTableSectionElement;
    private spinner = document.getElementById('table-spinner') as HTMLElement;
    private noResults = document.querySelector('#table-filtered-no-results') as HTMLElement;
    private noResultsLink = document.querySelector('#table-filtered-no-results-link') as HTMLAnchorElement;

    private editVisibleColumns = document.getElementById('table-edit-visible-columns') as HTMLAnchorElement | null;
    private clearSearch = document.getElementById('table-clear-search') as HTMLAnchorElement | null;
    private resetSortOrder = document.getElementById('table-reset-sort-order') as HTMLAnchorElement | null;
    private changePageSize = document.getElementById('table-change-page-size') as HTMLAnchorElement | null;
    private filterChecked = document.getElementById('table-filter-checked') as HTMLAnchorElement | null;
    private exportCSV = document.getElementById('table-export-csv') as HTMLAnchorElement | null;

    private otherCheckboxesChecked = document.getElementById('table-other-checkboxes-checked')!;

    private rows: row<R, C, D>[] = [];

    RowHeight = 37; // magic
    private rowBufferSize: number = this.getRowBufferSize();

    private currentBoundingIndexes: { start: number; end: number } = { start: 0, end: 0 };

    private defaultSort?: string;

    private timeout?: ReturnType<typeof setTimeout>;
    private autoUpdating = true;
    private interval: number = 60 * 1000;

    private drawAnimationFrame?: number;

    GetRowsFromResponse: (d: any) => R[] | null = () => [];
    DrawRow?: (tr: HTMLTableRowElement, row: R) => void;

    OnSort?: (ids: { id: string | number, newIndex: number }[], row: R) => void;
    private currentlyDragging?: number;
    private dummyRow = document.createElement('tr');

    private mouseMoving = false;
    private mouseMovingTimeout?: ReturnType<typeof setTimeout>;

    private built = false;
    private fullGetInProgress = false;

    private headersDrawn = false;

    AlwaysOnColumns: C[] = [];
    AlwaysOnData: D[] = [];

    private tableHeaderWidths: number[] = this.getTableHeaderWidths();

    private lastScroll: number = window.scrollY;

    Checkboxes = false;
    private checkboxesID = Number(G().Get('chk')) || 0;
    private checkboxValues: Set<id> = new Set();
    private headerCheckbox?: HTMLInputElement;
    private headerCheckboxBadge?: HTMLElement;
    private lastClickCheckboxIndex?: number;

    private defaultVisibleColumns?: string[];

    private dirtyColumnHeaders = false;

    readonly AvailableFields: Set<string> = new Set();

    private cancelTokenSource?: CancelTokenSource;

    DownloadChunkSize = 1_000;

    constructor(private dataURL: string, public SearchInput: HTMLInputElement, public SearchForm: HTMLFormElement) {
        this.nextPage.forEach(e => e.addEventListener('click', e => {
            e.preventDefault();
            this.NextPage();
        }));
        this.prevPage.forEach(e => e.addEventListener('click', e => {
            e.preventDefault();
            this.PrevPage();
        }));

        if (this.page) {
            this.prevPage.forEach(e => e.removeAttribute('disabled'));
        }

        window.addEventListener('scroll', () => {
            if (Math.abs(this.lastScroll - window.scrollY) < this.RowHeight) {
                return;
            }
            this.lastScroll = window.scrollY;

            setTimeout(() => this.drawRows());
        });

        window.addEventListener('resize', () => {
            this.rowBufferSize = this.getRowBufferSize();
            this.updateTableHeaderWidths();

            this.drawRows();
        });

        // check if the thead is stuck to the top of the page
        const observer = new IntersectionObserver(entries => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    this.thead.classList.remove('shadow-sm');
                } else {
                    this.thead.classList.add('shadow-sm');
                }
            }
        }, {
            rootMargin: '-42px', // magic don't touch
        });
        observer.observe(this.thead);

        this.table.addEventListener('mousemove', () => {
            this.mouseMoving = true;
            if (this.mouseMovingTimeout) {
                clearTimeout(this.mouseMovingTimeout);
            }
            this.mouseMovingTimeout = setTimeout(() => this.mouseMoving = false, 500);
        });

        window.addEventListener('focus', () => {
            if (this.autoUpdating) {
                clearTimeout(this.timeout!);
                this.Send(true);
            }
        });

        this.tbody.style.position = 'relative';

        this.editVisibleColumns?.addEventListener('click', e => {
            e.preventDefault();

            const ul = document.createElement('ul');
            ul.classList.add('list-group', 'list-group-flush');

            for (const c of this.columns) {
                let name = c.Name;
                if (typeof c.Tooltip === 'string') {
                    name = c.Tooltip;
                }

                const li = document.createElement('li');
                li.textContent = name;
                li.classList.add('list-group-item', 'form-switch', 'd-flex');

                if (c.HeadingHTML) {
                    const span = document.createElement('span');
                    span.classList.add('text-muted', 'ms-1');
                    span.innerHTML = `/ ${c.HeadingHTML}`;
                    li.appendChild(span);
                }

                const input = document.createElement('input');
                input.type = 'checkbox';
                input.checked = c.Visible;
                input.classList.add('form-check-input', 'ms-auto');
                li.append(input);

                input.addEventListener('click', () => {
                    if (input.checked) {
                        this.ShowColumn(c);
                    } else {
                        this.HideColumn(c);
                    }
                });

                ul.appendChild(li);
            }

            const m = new Modal({
                title: 'Show/Hide columns',
                size: 'modal-sm',
                body: ul,
                confirmText: 'Reset to Defaults',
                buttons: [{
                    class: 'btn btn-outline-secondary text-body',
                    text: 'Reset to defaults',
                    click: () => {
                        this.ResetVisibleColumns();
                        this.Send();
                        m.Close();
                    },
                }],
            });

            m.Element.querySelector('.modal-body')?.classList.remove('modal-body');
        });

        this.clearSearch?.addEventListener('click', e => {
            e.preventDefault();
            this.Query.Reset();
        });

        this.resetSortOrder?.addEventListener('click', e => {
            e.preventDefault();
            this.ClearSort();
            this.Send();
        });

        this.changePageSize?.addEventListener('click', e => {
            e.preventDefault();

            const body = (document.querySelector('#modal-page-size-template') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

            const pageSize = body.querySelector('#modal-page-size') as HTMLInputElement;
            const defaultPageSize = body.querySelector('#modal-default-page-size') as HTMLElement;

            pageSize.value = String(this.GetPageSize());
            defaultPageSize.textContent = this.RowsPerPage.toLocaleString();

            new Modal({
                title: "Adjust Page Size",
                body,
                onSubmit: async () => {
                    this.SetPageSize(Number(pageSize.value));
                    this.Send();
                },
            });
        });

        this.filterChecked?.addEventListener('click', e => {
            e.preventDefault();

            this.Query.AddFilter('id', this.CheckboxValues().join(','));
        });

        this.exportCSV?.addEventListener('click', async e => {
            e.preventDefault();

            let j = 0;
            let csv = '';
            const cols = this.columns.filter(c => c.Visible);
            const colsWithID = this.columns.filter(c => c.Visible || c.Name === 'ID');
            const writeValue = (s: string) => {
                if (!s) {
                    return;
                }

                if (s.indexOf(`\r`) !== -1 ||
                    s.indexOf(`\n`) !== -1 ||
                    s.indexOf(`\\`) !== -1 ||
                    s.indexOf(`"`) !== -1 ||
                    s.indexOf(`,`) !== -1) {

                    csv += `"${s.replace(/[\\"]/g, '\\$0')}"`;
                } else {
                    csv += s;
                }
            };

            for (const c of cols) {
                if (j++) {
                    csv += `,`;
                }

                if (c.Name === 'ID') {
                    writeValue(`SMG ${c.Name}`);
                } else {
                    writeValue(c.Name);
                }
            }

            const query = String(this.Query);
            const apiColumns = this.getAPIColumns().join(',');

            const body = (document.querySelector('#modal-page-download-progress-template') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

            const progress = body.querySelector('.progress-bar') as HTMLElement;

            const status = body.querySelector('#modal-status') as HTMLElement;

            let canceled = false;
            const m = new Modal({
                title: 'Downloading all records to CSV',
                body,
                onClose: () => canceled = true,
            });

            const count = (await this.GetColumnStats('id')).count;

            let k = 0;
            let lastID: any;

            const updateStatus = () => {
                status.textContent = `Downloading records... ${k}/${count}`;
                progress.style.width = `${Math.min(k / count * 100, 100)}%`;
            };
            updateStatus();

            while (true) {
                if (canceled) {
                    return;
                }

                let q = query;
                if (lastID) {
                    q += ` id:>${lastID}`;
                }

                const rows = await axios.get(this.dataURL, {
                    params: {
                        'q': q,
                        'max': this.DownloadChunkSize,
                        'sort': 'id asc',
                        'data': apiColumns,
                    },
                }).then(resp => this.GetRowsFromResponse(resp.data) ?? []);

                if (!rows.length) {
                    break;
                }

                for (const r of rows) {
                    csv += `\r\n`;

                    j = 0;
                    for (const c of colsWithID) {
                        const t = c.CellText(r);
                        if (c.Name === 'ID') {
                            lastID = t;
                        }

                        if (!c.Visible) {
                            continue;
                        }

                        if (j++) {
                            csv += `,`;
                        }

                        writeValue(await t);
                    }
                }

                k += rows.length;
                updateStatus();
            }

            Download(`${document.title} – ${DateTime.now().toISODate()}.csv`, csv, 'text/csv;charset=utf-8;');
            m.Close();
        });

        const saveViewButton: HTMLButtonElement | null = document.querySelector('#table-save-views-button');
        if (saveViewButton) {
            const dropdownButton = document.querySelector('#table-save-views-dropdown-button') as HTMLButtonElement;
            const dropdownMenu = document.querySelector('#table-save-views-dropdown') as HTMLElement;
            const resetLink = document.querySelector('#table-views-reset-link') as HTMLAnchorElement;

            resetLink.addEventListener('click', e => {
                e.preventDefault();

                G().Delete('sort');
                this.setSort('');

                this.ResetVisibleColumns();

                this.SetPageSize(0);

                this.Query.Set('');
            });

            saveViewButton.addEventListener('click', () => {
                const body = (document.querySelector('#modal-table-save-view-template') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

                const name = body.querySelector('#modal-name') as HTMLInputElement;
                const shared = body.querySelector('#modal-shared') as HTMLInputElement;

                new Modal({
                    title: "Create a new view",
                    body,
                    onSubmit: async () => axios.post('/table-views', {
                        'name': name.value,
                        'pageSize': this.GetUserPageSize(),
                        'query': G().Get('q') || '',
                        'sort': G().Get('sort') || '',
                        'page': window.location.pathname,
                        'shared': shared.checked,
                        'visibleColumns': this.GetVisibleColumnNames(),
                    }).
                        then(() => { toast.Success(`Your new view has been saved as "${name.value.trim()}"`); }),
                });

                setTimeout(() => name.focus());
            });
        }

        this.noResultsLink.addEventListener('click', e => {
            e.preventDefault();

            for (const f of this.Query.GetCurrentFilterNames()) {
                if (this.Query.FilterIsDefault(f)) {
                    this.Query.RemoveFilter(f, false);
                }
            }

            this.Query.Search();
        });
    }

    AddColumns(...column: column.Column<R, C, D>[]) {
        for (const c of column) {
            c.Table = this;
            this.columns.push(c);
        }
    }

    GetColumns(): column.Column<R, C, D>[] {
        return this.columns;
    }

    private handleFull(d: apiResponse) {
        if (d.nextPage) {
            this.nextPage.forEach(e => e.removeAttribute('disabled'));
        }

        d.returnedFields.map(s => s.toLowerCase()).forEach(v => this.AvailableFields.add(v));

        for (const c of this.columns) {
            if (c.Visible) {
                for (const apiCol of c.APIColumns) {
                    if (!this.AvailableFields.has(String(apiCol).toLowerCase())) {
                        this.HideColumn(c.Name);
                        break;
                    }
                }
            }
        }

        const rows = this.GetRowsFromResponse(d) ?? [];
        this.rows.length = 0;
        for (const r of rows) {
            this.rows.push(new row(this, r, this.tbody));
        }
        this.currentBoundingIndexes = { start: 0, end: 0 };

        if (!this.headersDrawn) {
            this.drawHeaders();
            this.headersDrawn = true;
        }

        this.tbody.innerHTML = '';

        this.drawRowCounts();
        this.drawRows();

        if (!this.rows.length && G().Get('q') && this.Query.UsingDefaultFilters() && this.Query.HasTextSearch()) {
            this.noResults.classList.remove('d-none');
        } else {
            this.noResults.classList.add('d-none');
        }
    }

    private drawRowCounts() {
        if (this.rows.length) {
            this.startingRowNumber.textContent = String(this.page * this.GetPageSize() + 1);
            this.endingRowNumber.textContent = String((this.page) * this.GetPageSize() + this.rows.length);
            this.startingRowNumber.parentElement?.classList.remove('d-none');
        } else {
            this.startingRowNumber.parentElement?.classList.add('d-none');
        }
    }

    private handleError(err: string) {
        // tslint:disable-next-line: no-console
        console.error(err);

        // TODO: make a dialog func?
        const div = document.createElement('div');
        div.classList.add('modal');
        div.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">Oh no!</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <p>Something failed trying to get the table rows. The server's message was:</p>
                <p class="text-danger">${html.Escape(err)}</p>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Close</button>
            </div>
            </div>
        </div>`;
        document.body.appendChild(div);

        const m = new bootstrap.Modal(div);
        m.show();
    }

    GetRows(): R[] {
        return [...this.rows.map(r => r.row)];
    }

    private handleUpdate(d: any) {
        if (this.updatesPaused()) {
            return;
        }

        const newRows = this.GetRowsFromResponse(d) ?? [];
        const newRowIndex: { [id: string]: { row: R; index: number } } = {};
        let i = 0;
        for (const r of newRows) {
            newRowIndex[r.id!] = { row: r, index: i++ };
        }

        let hadUpdates = false;

        // updated rows
        for (const r of this.rows) {
            if (r.row.id! in newRowIndex && !equal(r.row, newRowIndex[r.row.id!].row)) {
                hadUpdates = true;

                r.row = newRowIndex[r.row.id!].row;

                if (r.tr) {
                    if (r.Visible()) {
                        const oldRow = r.tr;
                        r.tr = this.CreateRowByIndex(this.getIndexByID(r.row.id!)!);
                        r.tr.style.display = 'none';

                        this.tbody.replaceChild(r.tr, oldRow);

                        r.tr.classList.add('updated');
                        setTimeout(() => {
                            r.tr!.classList.add('transitioning');
                            r.tr!.classList.remove('updated');
                        });
                        setTimeout(() => r.tr!.classList.remove('transitioning'), 1000);
                    } else {
                        this.tbody.removeChild(r.tr);
                        r.tr = null;
                    }
                }
            }
        }

        const currentRowIDs = new Set(this.rows.map(r => r.row.id));

        // new rows
        for (const r of newRows) {
            if (!currentRowIDs.has(r.id)) {
                hadUpdates = true;

                const newRow = new row(this, r, this.tbody);
                this.rows.push(newRow);

                setTimeout(() => {
                    if (!newRow.tr) {
                        return;
                    }

                    newRow.tr.classList.add('created');
                    setTimeout(() => {
                        newRow.tr!.classList.add('transitioning');
                        newRow.tr!.classList.remove('created');
                    });
                    setTimeout(() => newRow.tr!.classList.remove('transitioning'), 1000);
                });
            }
        }

        // updated sort order
        let sortUpdated = false;
        i = 0;
        for (const r of this.rows) {
            if (r.row.id! in newRowIndex) {
                if (i !== newRowIndex[r.row.id!].index) {
                    sortUpdated = true;
                    hadUpdates = true;
                    break;
                }

                i++;
            }
        }

        if (sortUpdated) {
            const getSortIndex = (r: row<R, C, D>) => newRowIndex[r.row.id!]?.index ?? this.rows.indexOf(r);
            this.rows.sort((a, b) => getSortIndex(a) - getSortIndex(b));
        }

        if (hadUpdates) {
            this.drawRows(true);
            this.drawRowCounts();
        }

        let hadDeletes = false;

        // deleted rows
        i = 0;
        for (const r of this.rows) {
            if (!(r.row.id! in newRowIndex)) {
                hadDeletes = true;
                if (r.Visible()) {
                    r.tr!.classList.add('fade');
                    setTimeout(() => this.tbody.removeChild(r.tr!), 150);
                }
                continue;
            }
            this.rows[i++] = r;
        }
        this.rows.length = i;

        if (hadDeletes) {
            setTimeout(() => {
                this.drawRows();
                this.drawRowCounts();

                if (this.rows.length) {
                    this.noResults.classList.add('d-none');
                }
            }, 150);
        }

        if (this.rows.length) {
            this.noResults.classList.add('d-none');
        }
    }

    private getIndexByTr(tr: HTMLTableRowElement): number | null {
        for (let i = 0; i < this.rows.length; i++) {
            if (this.rows[i].tr === tr) {
                return i;
            }
        }

        return null;
    }

    private getIndexByID(id: number | string): number | null {
        for (let i = 0; i < this.rows.length; i++) {
            if (this.rows[i].row.id === id) {
                return i;
            }
        }

        return null;
    }

    private boundingRowIndexes(): { start: number, end: number } {
        const tbodyTop = this.tbody.getBoundingClientRect().top;
        const firstVisibleRow = Math.floor(tbodyTop * -1 / this.RowHeight);
        const lastVisibleRow = firstVisibleRow + Math.ceil(window.innerHeight / this.RowHeight);

        const firstBufferedRow = firstVisibleRow - this.rowBufferSize;
        const lastBufferedRow = lastVisibleRow + this.rowBufferSize;

        return { start: Math.max(firstBufferedRow, 0), end: Math.min(lastBufferedRow, this.rows.length - 1) };
    }

    private getTableHeaderWidths(): number[] {
        const widths: number[] = [];
        const tableHeaders = this.thead.querySelectorAll('th');
        for (let i = 0; i < tableHeaders.length; i++) {
            widths[i] = tableHeaders[i].getBoundingClientRect().width;
        }

        return widths;
    }

    private updateTableHeaderWidths() {
        this.tableHeaderWidths = this.getTableHeaderWidths();
        this.updateAllRowColumnWidths();
    }

    private updateAllRowColumnWidths() {
        for (const r of this.rows) {
            if (r.tr) {
                this.updateRowColumnWidths(r.tr);
            }
        }
    }

    private updateRowColumnWidths(tr: HTMLTableRowElement) {
        const tds = tr.children;
        for (let i = 0; i < tds.length; i++) {
            const div = tds[i].children[0] as HTMLElement;
            div.style.width = `${this.tableHeaderWidths[i]}px`;
        }
    }

    CreateRowByIndex(index: number): HTMLTableRowElement {
        const tr = document.createElement('tr');

        if (this.OnSort) {
            const td = html.FromString(`
            <td>
                <div class="p-0">
                    <a href="#" class="d-flex w-100 h-100 text-decoration-none align-items-center justify-content-center text-muted">
                        <i class="fas fa-grip-vertical"></i>
                    </a>
                </div>
            </td>`);
            this.initDragHandle(this.rows[index], td.querySelector('a')!);
            tr.appendChild(td);
        }

        if (this.Checkboxes) {
            const td = html.FromString(`
            <td>
                <div class="d-flex w-100 h-100 align-items-center justify-content-center">
                    <input class="form-check-input table-row-checkbox mt-0" type="checkbox" value="${html.Escape(String(this.rows[index].row.id))}">
                </div>
            </td>`);
            this.initRowCheckbox(this.rows[index], td.querySelector('input')!);
            tr.appendChild(td);
        }

        for (const c of this.columns) {
            if (!c.Visible) {
                continue;
            }

            tr.appendChild(c.Cell(this.rows[index].row));
        }

        this.updateRowColumnWidths(tr);

        if (this.DrawRow) {
            this.DrawRow(tr, this.rows[index].row);
        }

        tr.addEventListener('mouseenter', () => {
            if (typeof this.currentlyDragging !== 'undefined') {
                const myIndex = this.getIndexByTr(tr)!;

                this.arrayMove(this.rows, this.currentlyDragging, myIndex);
                this.drawRows();

                this.currentlyDragging = myIndex;
            }
        });

        return tr;
    }

    private arrayMove(arr: any[], oldIndex: number, newIndex: number) {
        if (newIndex >= arr.length) {
            let k = newIndex - arr.length + 1;
            while (k--) {
                arr.push(undefined);
            }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    }

    private getDummyRow(): HTMLTableRowElement {
        this.dummyRow.innerHTML = `<td colspan="${this.columns.filter(c => c.Visible).length + 1}"><div></div></td>`;
        return this.dummyRow;
    }

    private initDragHandle(row: row<R, C, D>, a: HTMLAnchorElement) {
        a.addEventListener('click', e => e.preventDefault());

        a.addEventListener('mousedown', e => {
            if (e.button !== 0) {
                return;
            }

            e.preventDefault();

            this.currentlyDragging = this.rows.indexOf(row);
            const startingIndex = this.currentlyDragging;

            const dummyTable = html.FromString(`
            <table class="table iap-table position-absolute shadow-lg dragging">
                <colgroup>
                    <col style="width:2rem"> <!-- for the drag handle itself -->
                </colgroup>
                <tbody></tbody>
            </table>`);
            const dummyTbody = dummyTable.querySelector('tbody')!;
            const dummyColgroup = dummyTable.querySelector('colgroup')!;
            dummyTable.style.minWidth = this.table.style.minWidth;
            dummyTable.style.width = getComputedStyle(this.table).width;

            const tr = row.tr!;

            const rect = tr.getBoundingClientRect(); // rekt

            row.tr = this.getDummyRow();
            row.tr.setAttribute('style', tr.getAttribute('style')!);
            (row.tr.children[0].children[0] as HTMLElement).style.width = `${this.tableHeaderWidths.reduce((s, n) => s + n)}px`;
            this.tbody.insertBefore(row.tr, tr);

            tr.removeAttribute('style');

            const trTop = rect.top;
            const trLeft = rect.left;

            const offsetY = e.clientY - trTop;
            const offsetX = e.clientX - trLeft;

            for (const c of this.columns) {
                if (!c.Visible) {
                    continue;
                }

                const col = document.createElement('col');
                col.style.width = typeof c.Width === 'string' ? c.Width : `${c.Width * 100}%`;
                dummyColgroup.appendChild(col);
            }

            dummyTable.style.transform = `translateX(${trLeft + window.scrollX}px) translateY(${trTop + window.scrollY}px)`;
            dummyTbody.appendChild(tr);

            document.body.appendChild(dummyTable);

            const move = (e: MouseEvent) => {
                dummyTable.style.transform = `translateX( ${e.clientX - offsetX + window.scrollX}px ) translateY( ${e.clientY - offsetY + window.scrollY}px )`;
            };
            const up = () => {
                tr.setAttribute('style', row.tr!.getAttribute('style')!);
                this.tbody.replaceChild(tr, this.rows[this.currentlyDragging!].tr!);
                this.rows[this.currentlyDragging!].tr = tr;

                const ids: { id: string | number, newIndex: number }[] = [];
                for (let i = Math.min(startingIndex, this.currentlyDragging!); i <= Math.max(startingIndex, this.currentlyDragging!); i++) {
                    ids.push({
                        id: this.rows[i].row.id!,
                        newIndex: i,
                    });
                }

                if (ids.length > 1) {
                    this.OnSort!(ids, row.row);
                }

                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);

                delete this.currentlyDragging;
            };

            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        });
    }

    Check(row: row<R, C, D>) {
        if (row.checkbox) {
            row.checkbox.checked = true;
        }
        this.checkboxValues.add(row.row.id!);
    }

    Uncheck(row: row<R, C, D>) {
        if (row.checkbox) {
            row.checkbox.checked = false;
        }
        this.checkboxValues.delete(row.row.id!);
    }

    IsChecked(id: id): boolean {
        return this.checkboxValues.has(id);
    }

    CheckboxValues(): id[] {
        return Array.from(this.checkboxValues.values());
    }

    ClearCheckboxes() {
        if (!this.checkboxesID) {
            return;
        }

        this.checkboxValues.clear();
        G().Delete('chk');
        window.sessionStorage.removeItem(`checkboxes:${this.checkboxesID}`);
        window.sessionStorage.setItem(`checkboxes:max`, JSON.stringify(this.checkboxesID - 1));
        this.checkboxesID = 0;

        for (const r of this.rows) {
            if (!r.checkbox) {
                continue;
            }

            r.checkbox.checked = false;
        }

        this.updateCheckboxEffects();
    }

    CheckAllCheckboxesOnPage() {
        for (const r of this.rows) {
            this.Check(r);
        }

        this.updateCheckboxEffects();
        this.updateCheckboxStore();
    }

    UncheckAllCheckboxesOnPage() {
        for (const r of this.rows) {
            this.Uncheck(r);
        }

        this.updateCheckboxEffects();
        this.updateCheckboxStore();
    }

    CurrentPageAllChecked(): boolean {
        if (!this.rows.length) {
            return false;
        }

        for (const r of this.rows) {
            if (!r.Checked()) {
                return false;
            }
        }

        return true;
    }

    CheckboxesCheckedOnOtherPage(): boolean {
        if (this.checkboxValues.size > this.rows.length) {
            return true;
        }

        const currentPageIDs = new Set(this.rows.map(r => r.row.id));
        for (const id of Array.from(this.checkboxValues.values())) {
            if (!currentPageIDs.has(id)) {
                return true;
            }
        }

        return false;
    }

    AtLeastOneCheckedOnPage(): boolean {
        for (const r of this.rows) {
            if (r.Checked()) {
                return true;
            }
        }

        return false;
    }

    private updateCheckboxStore() {
        if (!this.checkboxesID) {
            this.checkboxesID = (Number(window.sessionStorage.getItem(`checkboxes:max`)) || 0) + 1;
            G().Set('chk', String(this.checkboxesID));
            window.sessionStorage.setItem(`checkboxes:max`, JSON.stringify(this.checkboxesID));
        }
        window.sessionStorage.setItem(`checkboxes:${this.checkboxesID}`, JSON.stringify(Array.from(this.checkboxValues.values())));
    }

    private initRowCheckbox(row: row<R, C, D>, input: HTMLInputElement) {
        row.checkbox = input;

        input.checked = this.IsChecked(row.row.id!);

        input.addEventListener('click', e => {
            if (input.checked) {
                this.Check(row);

                const currentIndex = this.getIndexByID(row.row.id!)!;

                if (e.shiftKey && typeof this.lastClickCheckboxIndex === 'number' && this.lastClickCheckboxIndex !== currentIndex) {
                    const start = Math.min(this.lastClickCheckboxIndex, currentIndex);
                    const end = Math.max(this.lastClickCheckboxIndex, currentIndex);
                    for (let i = start + 1; i < end; i++) {
                        this.Check(this.rows[i]);
                    }
                }
                this.lastClickCheckboxIndex = currentIndex;
            } else {
                this.Uncheck(row);
                this.lastClickCheckboxIndex = undefined;
            }

            this.updateCheckboxEffects();
            this.updateCheckboxStore();
        });
    }

    private updateCheckboxEffects() {
        const elements = document.querySelectorAll('.table-disabled-with-checkboxes');
        for (const e of Array.from(elements)) {
            if (this.checkboxValues.size) {
                e.removeAttribute('disabled');
            } else {
                e.setAttribute('disabled', 'disabled');
            }
        }

        if (this.headerCheckbox) {
            if (this.CurrentPageAllChecked()) {
                this.headerCheckbox.checked = true;
                this.headerCheckbox.indeterminate = false;
            } else if (this.AtLeastOneCheckedOnPage()) {
                this.headerCheckbox.checked = false;
                this.headerCheckbox.indeterminate = true;
            } else {
                this.headerCheckbox.checked = false;
                this.headerCheckbox.indeterminate = false;
            }
        }

        if (this.CheckboxesCheckedOnOtherPage()) {
            this.otherCheckboxesChecked.classList.remove('d-none');
        } else {
            this.otherCheckboxesChecked.classList.add('d-none');
        }

        if (this.headerCheckboxBadge) {
            if (this.checkboxValues.size) {
                this.headerCheckboxBadge.classList.remove('d-none');
                this.headerCheckboxBadge.textContent = this.checkboxValues.size.toLocaleString();
            } else {
                this.headerCheckboxBadge.classList.add('d-none');
            }
        }
    }

    GetRowCount(): number {
        return this.rows.length;
    }

    private getRowBufferSize(): number {
        return Math.ceil(Math.ceil(window.innerHeight / this.RowHeight) * .50);
    }

    private lastRows?: number;

    private drawRows(immediate = false) {
        if (this.rows.length === 0) {
            this.tbody.style.height = '';
            return;
        }

        const render = () => {
            const { start, end } = this.boundingRowIndexes();

            if (this.lastRows !== this.rows.length) {
                this.lastRows = this.rows.length;

                this.tbody.style.height = `${this.rows.length * this.RowHeight}px`;
                this.updateTableHeaderWidths();
            }

            const lastPossibleRow = Math.min(this.currentBoundingIndexes.end, this.rows.length - 1);
            const absoluteStart = Math.min(start, end, this.currentBoundingIndexes.start, lastPossibleRow);
            const absoluteEnd = Math.max(start, end, this.currentBoundingIndexes.start, lastPossibleRow);

            const show: HTMLTableRowElement[] = [];
            const hide: HTMLTableRowElement[] = [];

            for (let i = absoluteStart; i <= absoluteEnd; i++) {
                const r = this.rows[i];

                if (start <= i && i <= end) {
                    if (!r.tr) {
                        r.tr = this.CreateRowByIndex(i);
                        r.tr.style.display = 'none';
                        this.tbody.appendChild(r.tr);
                    }

                    r.tr.style.position = 'absolute';
                    r.tr.style.top = `${i * this.RowHeight + 1}px`;
                    r.tr.style.left = '0';
                    r.tr.style.height = `${this.RowHeight}px`;

                    show.push(r.tr);
                } else if (!(start <= i && i <= end) && r.tr) {
                    hide.push(r.tr);
                }
            }

            for (const tr of show) {
                tr.style.display = '';
            }
            for (const tr of hide) {
                tr.style.display = 'none';
            }

            this.currentBoundingIndexes = { start, end };

            delete this.drawAnimationFrame;
        };

        if (immediate) {
            render();
            return;
        }

        if (this.drawAnimationFrame) {
            return;
        }

        this.drawAnimationFrame = requestAnimationFrame(render);
    }

    ClearSort(except?: column.Column<R, C, D>) {
        for (const c of this.columns) {
            if (c.Sort && c !== except) {
                c.SetSortDirection();
            }
        }
    }

    private drawHeaders() {
        let totalMinWidth = 0;

        const tr = document.createElement('tr');

        if (this.OnSort) {
            const th = document.createElement('th');
            th.style.width = '2rem';
            tr.appendChild(th);

            totalMinWidth += RemToPx(2);
        }

        if (this.Checkboxes) {
            const th = html.FromString(`
            <th class="position-relative" style="width:32px">
                <input class="form-check-input table-row-checkbox" type="checkbox" value="">
                <span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger d-none" style="top:0.5rem!important;opacity:.85">1,200</span>
            </th>`);

            this.headerCheckbox = th.querySelector('input')!;
            this.headerCheckboxBadge = this.headerCheckbox.parentElement!.querySelector('.badge') as HTMLElement;

            this.headerCheckbox.addEventListener('click', () => {
                if (this.headerCheckbox!.checked) {
                    this.CheckAllCheckboxesOnPage();
                } else {
                    this.UncheckAllCheckboxesOnPage();
                }
            });

            tr.appendChild(th);

            totalMinWidth += 32;
        }

        for (const c of this.columns) {
            if (!c.Visible) {
                continue;
            }

            const th = c.Header();
            if (c.Width) {
                if (typeof c.Width === 'string') {
                    th.style.width = c.Width;
                } else {
                    th.style.width = `${c.Width * 100}%`;
                }
            }

            let minWidth = c.MinWidth || c.Width;
            if (typeof minWidth === 'string') {
                if (minWidth.endsWith('rem')) {
                    minWidth = RemToPx(parseFloat(strings.TrimSuffix(minWidth, 'rem')));
                } else if (minWidth.endsWith('px')) {
                    minWidth = parseFloat(strings.TrimSuffix(minWidth, 'px'));
                } else {
                    throw Error(`can't convert width '${minWidth}' to px`);
                }
                totalMinWidth += minWidth;
            }

            tr.appendChild(th);
        }

        this.thead.innerHTML = '';
        this.thead.appendChild(tr);

        this.table.style.minWidth = `${totalMinWidth}px`;

        this.updateTableHeaderWidths();

        this.dirtyColumnHeaders = false;
    }

    private updatesPaused(): boolean {
        return this.mouseMoving || typeof this.currentlyDragging !== 'undefined' || this.fullGetInProgress || document.hidden;
    }

    private getAPIColumns(): C[] {
        const apiColumns: C[] = [...this.AlwaysOnColumns];
        for (const c of this.columns) {
            if (!c.Visible) {
                continue;
            }

            if (c.APIColumns.length) {
                apiColumns.push(...c.APIColumns);
            }
        }
        return Array.from(new Set(apiColumns));
    }

    private getAPIData(): D[] {
        const apiData: D[] = [...this.AlwaysOnData];
        for (const c of this.columns) {
            if (!c.Visible) {
                continue;
            }
            if (c.APIData.length) {
                apiData.push(...c.APIData);
            }
        }
        return Array.from(new Set(apiData));
    }

    async Send(update = false, firstRun = false) {
        if (update) {
            if (this.updatesPaused()) {
                if (this.autoUpdating) {
                    this.ScheduleAutoUpdate();
                }
                return;
            }
        } else {
            // TODO: I'm making a mess, I want these to be cancellable
            // No idea what this does to a request that's already being drawn?
            // if (this.fullGetInProgress) {
            //     return;
            // }
            this.fullGetInProgress = true;
            // TODO: this is the wrong spot for this. The table has nothing to do with the query string
            this.setQuerystring();
        }

        this.cancelTokenSource?.cancel();
        this.cancelTokenSource = axios.CancelToken.source();

        const columns = this.columns.filter(c => c.Sort?.direction);
        const sort: { column: C, direction: 'asc' | 'desc', index: number }[] = [];
        for (const c of columns) {
            if (c.Sort?.direction) {
                sort.push({
                    column: c.Sort.column,
                    direction: c.Sort.direction,
                    index: (c.Sort.index || 0) * 100,
                });

                if (c.Sort.columns?.length) {
                    let i = 1;
                    for (const s of c.Sort.columns) {
                        sort.push({
                            column: s.column,
                            direction: c.Sort.direction === 'asc' ? s.direction : (s.direction === 'asc' ? 'desc' : 'asc'),
                            index: (c.Sort.index || 0) * 100 + (i++),
                        });
                    }
                }
            }
        }
        sort.sort((a, b) => a.index - b.index);

        if (columns.length > 1) {
            this.columns.forEach(c => c.Sort?.direction ? c.ShowIndex() : c.HideIndex());
        } else {
            this.columns.forEach(c => c.HideIndex());
        }

        if (this.page) {
            G().Set('p', String(this.page));
        } else {
            G().Delete('p');
        }

        return axios.get(this.dataURL, {
            cancelToken: this.cancelTokenSource.token,
            params: {
                'q': String(this.Query),
                'max': this.GetPageSize(),
                'p': this.page,
                'sort': this.getSortQuerystring() || this.defaultSort,
                'data': this.getAPIColumns().join(','),
                'extra': this.getAPIData().join(','),
            },
        }).
            then(resp => {
                // hide all tooltips
                for (const e of Array.from(document.querySelectorAll('[aria-describedby*="tooltip"]'))) {
                    bootstrap.Tooltip.getInstance(e)?.hide();
                }

                this.reload.removeAttribute('disabled');

                this.spinner.classList.add('d-none');

                if (update) {
                    this.handleUpdate(resp.data);
                } else {
                    this.handleFull(resp.data);
                }
            }).
            catch(err => this.handleError(err)).
            finally(() => {
                if (firstRun) {
                    this.built = true;
                }

                if (this.autoUpdating) {
                    this.ScheduleAutoUpdate();
                }

                if (!update) {
                    this.fullGetInProgress = false;
                }

                this.updateCheckboxEffects();
                this.lastClickCheckboxIndex = undefined;

                if (this.dirtyColumnHeaders) {
                    this.drawHeaders();
                }
            });
    }

    async GetColumnStats<C>(column: C): Promise<ColumnStats> {
        return axios.get<ColumnStats>(this.dataURL, {
            params: {
                'q': String(this.Query),
                'colStats': column,
            },
        }).then(resp => resp.data);
    }

    AutoUpdateScheduled(): boolean {
        return Boolean(this.timeout);
    }

    DisableAutoUpdating() {
        clearTimeout(this.timeout!);
        this.autoUpdating = false;
    }

    EnableAutoUpdating(timeout?: number) {
        if (timeout) {
            this.interval = timeout;
        }

        if (!this.AutoUpdateScheduled()) {
            this.ScheduleAutoUpdate();
        }
        this.autoUpdating = true;
    }

    ScheduleAutoUpdate() {
        if (!this.built) {
            return;
        }

        clearTimeout(this.timeout!);
        this.timeout = setTimeout(() => this.Send(true), Math.max(this.interval, document.hasFocus() ? this.interval : 60 * 1000));
    }

    private getSortQuerystring(): string {
        const columns = this.columns.filter(c => c.Sort?.direction);
        columns.sort((a, b) => (a.Sort?.index ?? 0) - (b.Sort?.index ?? 0));

        let q = '';
        let i = 0;
        for (const c of columns) {
            if (i++) {
                q += ',';
            }
            q += `${c.Sort!.column} ${c.Sort!.direction}`;
        }

        return q;
    }

    private setQuerystring() {
        const sort = this.getSortQuerystring();

        if (!sort || sort === this.defaultSort) {
            G().Delete('sort');
        } else {
            G().Set('sort', sort);
        }
    }

    NextPage() {
        this.nextPage.forEach(e => e.setAttribute('disabled', 'disabled'));
        this.prevPage.forEach(e => e.removeAttribute('disabled'));
        this.page++;
        this.Send();
    }

    PrevPage() {
        this.page--;
        if (this.page <= 0) {
            this.prevPage.forEach(e => e.setAttribute('disabled', 'disabled'));
        }
        this.Send();
    }

    SetDefaultSort(s: string) {
        this.defaultSort = s;
    }

    private setSort(sort: string) {
        this.ClearSort();
        for (let s of sort.split(',')) {
            const direction = s.endsWith(' asc') ? 'asc' : 'desc';
            s = strings.TrimSuffix(s, ` ${direction}`).toLowerCase();

            for (const c of this.columns) {
                if (String(c.Sort?.column).toLowerCase() === s) {
                    c.SetSortDirection(direction);
                }
            }
        }
    }

    async Build() {
        this.defaultVisibleColumns = this.GetDefaultVisibleColumnNames();

        if (this.checkboxesID) {
            const values: id[] = JSON.parse(window.sessionStorage.getItem(`checkboxes:${this.checkboxesID}`) || '');
            for (const v of values) {
                this.checkboxValues.add(v);
            }
        }

        if (this.checkboxesID && !this.checkboxValues.size) {
            this.ClearCheckboxes();
        }

        for (const c of this.columns) {
            c.Table = this;
        }

        this.defaultSort ??= this.getSortQuerystring();

        const sort = (G().Get('sort') || this.defaultSort).trim();
        if (sort) {
            this.setSort(sort);
        }

        this.reload.addEventListener('click', e => {
            e.preventDefault();

            this.reload.setAttribute('disabled', 'disabled');
            this.Send();
        });

        if (this.searchForm) {
            this.searchForm.addEventListener('submit', e => {
                e.preventDefault();
                this.Send();
            });
            this.searchForm.addEventListener('changed.iap.tokens', () => this.Send());
        }

        await this.Send(false, true);

        this.otherCheckboxesChecked.querySelector('a')!.addEventListener('click', e => {
            e.preventDefault();

            const currentPageIDs = new Set(this.rows.map(r => r.row.id));
            for (const id of Array.from(this.checkboxValues.values())) {
                if (!currentPageIDs.has(id)) {
                    this.checkboxValues.delete(id);
                }
            }

            this.updateCheckboxEffects();
        });
    }

    DeleteRow(row: R) {
        const index = this.getIndexByID(row.id!);
        if (index === null) {
            return;
        }

        const r = this.rows[index];

        if (r.tr) {
            this.tbody.removeChild(r.tr);
        }
        this.rows.splice(index, 1);

        this.drawRows();
        this.drawRowCounts();

        if (r.Checked()) {
            this.Uncheck(r);
        }
        this.updateCheckboxEffects();
    }

    GetColumnByName(columnName: string): column.Column<R, C, D> | null {
        for (const c of this.columns) {
            if (c.Name.toLowerCase() === String(columnName).toLowerCase()) {
                return c;
            }
        }

        return null;
    }

    HideColumn(c: string | column.Column<R, C, D>) {
        if (!(c instanceof column.Column)) {
            const columnObject = this.GetColumnByName(c);
            if (!columnObject) {
                return;
            }

            c = columnObject;
        }

        const visibleColumns = this.columns.filter(c => c.Visible);

        const visibleColumnsIndex = visibleColumns.indexOf(c);
        if (visibleColumnsIndex === -1) {
            return;
        }

        let htmlIndex = visibleColumnsIndex;
        if (this.OnSort) {
            htmlIndex++;
        }
        if (this.Checkboxes) {
            htmlIndex++;
        }

        const tr = this.thead.querySelector('tr') as HTMLTableRowElement;

        const hiddenCol = tr.children[htmlIndex];
        tr.children[htmlIndex]?.parentElement?.removeChild(hiddenCol);

        for (const r of this.rows) {
            if (r.tr) {
                const hiddenCol = r.tr.children[htmlIndex];
                r.tr.children[htmlIndex]?.parentElement?.removeChild(hiddenCol);
            }
        }

        c.Visible = false;

        this.updateTableHeaderWidths();
        this.drawRows();

        window.localStorage.setItem(`${window.location.pathname}columnVisibility/${c.Name}`, JSON.stringify(false));
    }

    ShowColumn(c: string | column.Column<R, C, D>, send = true) {
        if (!(c instanceof column.Column)) {
            const columnObject = this.GetColumnByName(c);
            if (!columnObject) {
                return;
            }

            c = columnObject;
        }

        const visibleColumns = this.columns.filter(c => c.Visible);

        const visibleColumnsIndex = visibleColumns.indexOf(c);
        if (visibleColumnsIndex !== -1) {
            return;
        }

        c.Visible = true;

        if (send) {
            this.Send().
                finally(() => this.drawHeaders());
        } else {
            this.dirtyColumnHeaders = true;
        }

        window.localStorage.setItem(`${window.location.pathname}columnVisibility/${c.Name}`, JSON.stringify(true));
    }

    HideAllExcept(...columnNames: string[]) {
        const columnNamesSet = new Set(columnNames);

        for (const c of this.columns) {
            if (c.Visible && !columnNamesSet.has(c.Name)) {
                this.HideColumn(c.Name);
            }
        }
    }

    async CopyColumn(c: column.Column<R, C, D>) {
        let s = '';

        let i = 0;
        for (const r of this.rows) {
            const t = await c.CellText(r.row);
            if (!t.length) {
                continue;
            }

            if (i) {
                s += `\n`;
            }

            s += t;

            i++;
        }

        clipboard.Copy(s);
    }

    static Map<R extends GenericRow>(o: { [key: string]: R }, a?: R[]): void {
        if (a) {
            Object.keys(o).forEach(k => delete o[k]);
            for (const r of a) {
                if (!r.id) {
                    continue;
                }
                o[r.id] = r;
            }
        }
    }

    GetUserPageSize(): number {
        return Number(window.localStorage.getItem(`${window.location.pathname}:pageSize`)) || 0;
    }

    GetPageSize(): number {
        return this.GetUserPageSize() || this.RowsPerPage;
    }

    SetPageSize(pageSize: number) {
        window.localStorage.setItem(`${window.location.pathname}:pageSize`, String(pageSize));
    }

    GetDefaultVisibleColumnNames(): string[] {
        return this.columns.filter(c => c.DefaultVisibility).map(c => c.Name);
    }

    GetVisibleColumnNames(): string[] {
        return this.columns.filter(c => c.Visible).map(c => c.Name);
    }

    SetVisibleColumns(columnNames: string[]) {
        const columnNamesSet = new Set(columnNames);

        for (const c of this.columns) {
            if (c.Visible && !columnNamesSet.has(c.Name)) {
                this.HideColumn(c.Name);
            } else if (!c.Visible && columnNamesSet.has(c.Name)) {
                this.ShowColumn(c.Name, false);
            }
        }
    }

    ResetVisibleColumns() {
        this.SetVisibleColumns(this.defaultVisibleColumns!);
    }
}
